import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { MembershipType, StripeSetupIntentType, API_ERROR_MAP } from 'types'
import { Spinner, Button } from 'lp-components'
import { ExternalLink } from 'components'
import AutoRenewalPaymentSetup from './AutoRenewalPaymentSetup'
import { PATH, makePath } from 'config'
import { calculateArbStartDate, getPaymentSubmitErrorMessage } from 'utils'
import { PAYMENT_STAGE, logPaymentFailed } from 'uxcam-service'

const propTypes = {
  clearStripeSetupIntent: PropTypes.func.isRequired,
  editView: PropTypes.bool.isRequired,
  fetchStripeSetupIntent: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  lastFour: PropTypes.string,
  membership: MembershipType.isRequired,
  onSuccess: PropTypes.func.isRequired,
  stripeCustomerId: PropTypes.string,
  stripeSetupIntent: StripeSetupIntentType,
  toggleEditView: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
}

function StripeAutoRenewalInfo({
  clearStripeSetupIntent,
  editView,
  fetchStripeSetupIntent,
  flashErrorMessage,
  lastFour = null,
  membership,
  onSuccess,
  stripeCustomerId = null,
  stripeSetupIntent = null,
  toggleEditView,
  update,
}) {
  const {
    communication_member_renewal_amount__c: renewalCost,
    membership_expiration_date__c: membershipExpirationDateString,
    account__r__heroku_external_id__c,
    membership_contact__r__heroku_external_id__c: contactId,
    heroku_external_id__c,
    sfid: membershipId,
  } = membership

  const [isOpeningEditView, setIsOpeningEditView] = useState(false)

  const setupIntentClientSecret = stripeSetupIntent?.client_secret
  const customerSessionClientSecret =
    stripeSetupIntent?.customer_session_client_secret

  // A SetupIntent is single-use, so every visit to the edit form gets a brand
  // new one. The fetch has to resolve before the form opens: Stripe ignores a
  // clientSecret that changes after <Elements> mounts.
  const handleOpenEditView = async () => {
    if (!stripeCustomerId) {
      return flashErrorMessage(API_ERROR_MAP.UNABLE_TO_EDIT_PAYMENT_METHOD)
    }

    setIsOpeningEditView(true)

    try {
      await clearStripeSetupIntent()
      await fetchStripeSetupIntent({ stripeCustomerId })
    } catch (error) {
      logPaymentFailed(PAYMENT_STAGE.SETUP_INTENT, error)
      return flashErrorMessage(API_ERROR_MAP.STRIPE_SETUP_INTENT_FAILED)
    } finally {
      setIsOpeningEditView(false)
    }

    toggleEditView()
  }

  // Best-effort cleanup: discarding must never become the error the member sees.
  const discardSetupIntent = async () => {
    await clearStripeSetupIntent().catch(() => {})
  }

  const handleSubmit = async (formValues) => {
    const payload = {
      membershipid: membershipId,
      amount: renewalCost,
      account__r__heroku_external_id__c,
      contact__r__heroku_external_id__c: contactId,
      membership__r__heroku_external_id__c: heroku_external_id__c,
      arb_start_date: calculateArbStartDate(membershipExpirationDateString),
      stripe_customer_id__c: stripeCustomerId,
      stripe_payment_method_id: formValues.stripe_payment_method_id,
    }

    // Reaching this function means confirmSetupIntent already succeeded, so the
    // intent is spent. On failure discard it and close the form, since reopening
    // is what mints a fresh one. On success it is deliberately left in place:
    // clearing here would re-render into the <Spinner /> guard below and unmount
    // this form mid-submit, and handleOpenEditView clears before every reopen
    // anyway.
    try {
      await update(payload)
    } catch (error) {
      logPaymentFailed(PAYMENT_STAGE.PAYMENT_METHOD_UPDATE, error)
      await discardSetupIntent()
      toggleEditView()
      throw error
    }
  }

  const handleError = (error) => {
    // Pre-confirm failures land here too (incomplete card fields, declines),
    // with the intent untouched and the member's input still in the form, so
    // only flash. handleSubmit owns closing the form when the intent is spent.
    flashErrorMessage(getPaymentSubmitErrorMessage(error))
  }

  // The card was already updated, so always close the form -- even if refreshing
  // the billing data fails.
  const handleSuccess = async () => {
    try {
      await onSuccess()
    } finally {
      toggleEditView()
    }
  }

  // Discard the unused intent on cancel so it can't leak into a later flow.
  const handleCloseEditView = () => {
    toggleEditView()
    discardSetupIntent()
  }

  if (!editView) {
    return (
      <div className="row">
        <div className="col-6">
          <h3>Payment Method</h3>
          <Button
            onClick={handleOpenEditView}
            submitting={isOpeningEditView}
            className="link-primary"
          >
            (Edit)
          </Button>
          {lastFour && <p>Ending in {lastFour}</p>}
        </div>
        <div className="col-6 align-right">
          <ExternalLink
            href={makePath(
              PATH.SEATOW,
              PATH.MEMBERSHIP,
              PATH.MEMBERSHIP_AGREEMENT,
            )}
            className="link-small"
          >
            Auto Renew Agreement
          </ExternalLink>
        </div>
      </div>
    )
  }

  if (!setupIntentClientSecret) return <Spinner />

  return (
    <AutoRenewalPaymentSetup
      stripeSetupIntentId={setupIntentClientSecret}
      customerSessionClientSecret={customerSessionClientSecret}
      closeEditForm={handleCloseEditView}
      onError={handleError}
      onSuccess={handleSuccess}
      onSubmit={handleSubmit}
      submitButtonText="Update Payment Method"
    />
  )
}

StripeAutoRenewalInfo.propTypes = propTypes

export default StripeAutoRenewalInfo
